import { useEffect, useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { IInboxNotice, InboxError, InboxNotice } from "../../components/messagesInbox/InboxBits";
import { LargeItemBanner } from "../../components/returns/LargeItem";
import ReceivedCheck, { RECEIVED_CHECK_ANCHOR } from "../../components/returns/ReceivedCheck";
import ReturnCard from "../../components/returns/ReturnCard";
import ReturnContextPanel from "../../components/returns/ReturnContextPanel";
import ReturnHeader from "../../components/returns/ReturnHeader";
import ReturnMessages from "../../components/returns/ReturnMessages";
import ReturnTimeline from "../../components/returns/ReturnTimeline";
import ReturnTrackingForm from "../../components/returns/ReturnTrackingForm";
import WhatEbayAllows, { ReturnPanelRequest } from "../../components/returns/WhatEbayAllows";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import { useReturn, useReturnsMutations } from "../../data/useReturns";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import { ReturnActionKind, ReturnActionRequest, ReturnActionResult } from "../../models/returns/IReturnsApi";
import isLocalDebug from "../../utils/isLocalDebug";
import { canAddReturnTracking, RefundNowPrefill } from "../../utils/returnsRules";
import NoAccess from "../Error/NoAccess";

const ACTION_KINDS: ReturnActionKind[] = ["accept", "full_refund_keep", "partial_offer", "decline", "provide_rma", "upload_label", "mark_received", "refund_full", "refund_deduction", "message", "escalate"];

function errorText(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

/** One return. Needs the "Returns" feature flag (relaxed on localhost). */
export default function ReturnDetail() {
  const { features } = useCustomerDetails();
  const enabled = useFeatureEnabled("Returns");
  const { id } = useParams<{ id: string }>();

  if (!features.data && !features.isError) {
    return (
      <div className="flex justify-center py-20">
        <LoadingWheel />
      </div>
    );
  }
  if (!enabled && !isLocalDebug()) return <NoAccess />;

  return <ReturnWorkspace key={id} id={id as string} />;
}

function ReturnWorkspace({ id }: { id: string }) {
  const { t } = useTranslation("returns");
  const { setInfo } = usePageNameContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { detail: query } = useReturn(id);
  const { act, message, addNote, deleteNote } = useReturnsMutations();
  const [now, setNow] = useState(() => DateTime.utc());
  const [notice, setNotice] = useState<IInboxNotice | null>(null);
  const [messageNotice, setMessageNotice] = useState<IInboxNotice | null>(null);
  const [panelRequest, setPanelRequest] = useState<ReturnPanelRequest | null>(null);

  const detail = query.data;

  useEffect(() => {
    setInfo({ name: t("Returns"), desc: detail?.part?.title ? t("Return · {{title}}", { title: detail.part.title }) : t("Return") });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setInfo is a new function each render
  }, [t, detail?.part?.title]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(DateTime.utc()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // Neighbours from the list this return was opened from, for prev / next.
  const ids: string[] = Array.isArray((location.state as { ids?: unknown } | null)?.ids) ? ((location.state as { ids: string[] }).ids as string[]) : [];
  const index = ids.indexOf(id);
  const prevId = index > 0 ? ids[index - 1] : undefined;
  const nextId = index >= 0 && index < ids.length - 1 ? ids[index + 1] : undefined;

  const actionParam = searchParams.get("action");
  const initialKind = ACTION_KINDS.find((k) => k === actionParam);

  // The list's scan / find box links here with #received-check.
  const focusCheck = location.hash === `#${RECEIVED_CHECK_ANCHOR}`;

  // Refund now (after a check) and a saved return tracking open a panel in What eBay allows now; nothing is sent until its Confirm.
  const onRefundNow = (prefill: RefundNowPrefill) => setPanelRequest({ kind: prefill.kind, prefill: { condition: prefill.condition, deductionPct: prefill.deductionPct }, nonce: Date.now() });
  const onTrackingSaved = () => setPanelRequest({ kind: "message", prefill: { templateKey: "own_tracking" }, nonce: Date.now() });

  const showOutcome = (set: (n: IInboxNotice) => void, result: ReturnActionResult, sentText: string) => {
    set(result.blocked ? { tone: "amber", text: result.message || t("Sending is switched off on this Wilma – nothing was sent.") } : { tone: "green", text: result.message || sentText });
  };

  const onAct = async (kind: ReturnActionKind, body: ReturnActionRequest) => {
    setNotice(null);
    try {
      const result = kind === "message" ? await message.mutateAsync({ id, body: body.message }) : await act.mutateAsync({ id, kind, body });
      showOutcome(setNotice, result, detail?.channel === "ebay" ? t("Sent to eBay.") : t("Done."));
    } catch (error) {
      setNotice({ tone: "red", text: errorText(error, t("Something went wrong – nothing was sent.")) });
    }
  };

  const onSend = async (body: string, note: boolean, mentions: string[]) => {
    setMessageNotice(null);
    try {
      if (note) {
        await addNote.mutateAsync({ id, body, mentions });
      } else {
        const result = await message.mutateAsync({ id, body });
        showOutcome(setMessageNotice, result, detail?.channel === "ebay" ? t("Sent to the buyer on eBay.") : t("Saved on the return."));
      }
    } catch (error) {
      setMessageNotice({ tone: "red", text: errorText(error, t("Couldn't send that.")) });
    }
  };

  const onDeleteNote = async (eventId: string) => {
    try {
      await deleteNote.mutateAsync({ id, eventId });
    } catch (error) {
      setMessageNotice({ tone: "red", text: errorText(error, t("Couldn't delete that note.")) });
    }
  };

  if (query.isError && !detail) return <InboxError text={errorText(query.error, t("Couldn't load this return."))} onRetry={() => query.refetch()} />;
  if (!detail) {
    return (
      <div className="flex justify-center py-20">
        <LoadingWheel />
      </div>
    );
  }

  const busy = act.isLoading || message.isLoading;

  return (
    <div className="flex min-h-full flex-col">
      <ReturnHeader detail={detail} now={now} prevId={prevId} nextId={nextId} />
      <div className="flex flex-wrap items-start gap-3 px-4 py-3.5 lg:flex-nowrap">
        <section aria-label={t("Return case")} className="flex min-w-0 flex-1 flex-col gap-3">
          {detail.largeItem && <LargeItemBanner item={detail.largeItem} />}
          {!detail.liveActions && detail.channel === "ebay" && (
            <InboxNotice notice={{ tone: "amber", text: t("Live actions are switched off on this Wilma: anything confirmed here is recorded but not sent to eBay.") }} />
          )}
          <ReturnCard detail={detail} />
          <ReceivedCheck detail={detail} focusOnOpen={focusCheck} onRefundNow={onRefundNow} />
          <WhatEbayAllows
            detail={detail}
            now={now}
            onAct={onAct}
            acting={busy}
            notice={notice}
            initialKind={initialKind}
            request={panelRequest}
            trackingSlot={canAddReturnTracking(detail) ? <ReturnTrackingForm detail={detail} onSaved={onTrackingSaved} /> : undefined}
          />
          <ReturnMessages detail={detail} onSend={onSend} onDeleteNote={onDeleteNote} sending={message.isLoading || addNote.isLoading || deleteNote.isLoading} />
          {messageNotice && <InboxNotice notice={messageNotice} />}
          <ReturnTimeline events={detail.timeline} deadline={detail.deadline} now={now} />
        </section>
        <ReturnContextPanel detail={detail} />
      </div>
    </div>
  );
}
